'use client'

import { useState } from 'react'
//import { z } from "zod";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/platform/v2/ui/dialog'
import { Button } from '@/components/platform/v2/ui/button'
import { Input } from '@/components/platform/v2/ui/input'
import { Label } from '@/components/platform/v2/ui/label'
import { Textarea } from '@/components/platform/v2/ui/textarea'
import { toast } from '@/hooks/use-toast'
import { cn } from '@/lib/utils'

/*const schema = z.object({
  business: z.string().trim().min(2, "Опишите бизнес").max(300),
  clientSource: z.string().trim().max(300).optional(),
  currentTools: z.string().trim().max(300).optional(),
  hasCrm: z.string(),
  hasSite: z.string(),
  needsApp: z.string(),
  improve: z.string().trim().max(600).optional(),
  name: z.string().trim().min(2, "Введите имя").max(100),
  contact: z.string().trim().min(5, "Оставьте телефон или email").max(120),
});*/

interface Props {
    open: boolean
    onOpenChange: (open: boolean) => void
    title?: string
    scenario?: string
}

const OPTIONS = {
    hasCrm: ['Есть', 'Нет', 'Не знаю'],
    hasSite: ['Есть', 'Нет', 'Устарел'],
    needsApp: ['Нужно', 'Не уверен', 'Не нужно'],
}

const Choice = ({
    label,
    value,
    options,
    onChange,
}: {
    label: string
    value: string
    options: string[]
    onChange: (v: string) => void
}) => (
    <div className="flex flex-col gap-2">
        <Label className="text-xs font-semibold text-muted-foreground">{label}</Label>
        <div className="flex flex-wrap gap-2">
            {options.map((o) => (
                <button
                    key={o}
                    type="button"
                    onClick={() => onChange(o)}
                    className={cn(
                        'rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors',
                        value === o
                            ? 'border-primary bg-primary/15 text-primary'
                            : 'border-border/60 text-foreground/75 hover:border-primary/40',
                    )}
                >
                    {o}
                </button>
            ))}
        </div>
    </div>
)

const SolutionLeadDialog = ({ open, onOpenChange, title = 'Разобрать мой процесс', scenario }: Props) => {
    const [form, setForm] = useState({
        business: '',
        clientSource: '',
        currentTools: '',
        hasCrm: '',
        hasSite: '',
        needsApp: '',
        improve: '',
        name: '',
        contact: '',
    })
    const [submitting, setSubmitting] = useState(false)

    const set = (key: keyof typeof form) => (v: string) => setForm((f) => ({ ...f, [key]: v }))

    /*const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      toast({ title: "Проверьте данные", description: parsed.error.issues[0].message, variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      // Заявка готова к передаче в CRM VCENTRE.ONE
      const leads = JSON.parse(localStorage.getItem("solution-leads") || "[]");
      leads.push({ ...parsed.data, scenario: scenario ?? null, source: title, createdAt: new Date().toISOString() });
      localStorage.setItem("solution-leads", JSON.stringify(leads));
      toast({ title: "Заявка отправлена", description: "Свяжемся и разберём ваш сценарий." });
      onOpenChange(false);
      setForm({
        business: "",
        clientSource: "",
        currentTools: "",
        hasCrm: "",
        hasSite: "",
        needsApp: "",
        improve: "",
        name: "",
        contact: "",
      });
    } finally {
      setSubmitting(false);
    }
  };*/

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[88vh] max-w-lg overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                    <DialogDescription>
                        Несколько коротких вопросов — и мы покажем, как ваш процесс может собираться в VCENTRE.ONE.
                    </DialogDescription>
                </DialogHeader>

                <form /*onSubmit={submit}*/ className="mt-2 flex flex-col gap-4">
                    <div className="flex flex-col gap-2">
                        <Label htmlFor="sl-business" className="text-xs font-semibold text-muted-foreground">
                            Что у вас за бизнес?
                        </Label>
                        <Input
                            id="sl-business"
                            value={form.business}
                            onChange={(e) => set('business')(e.target.value)}
                            placeholder="Например: клиника, магазин, B2B-поставки"
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label htmlFor="sl-source" className="text-xs font-semibold text-muted-foreground">
                            Откуда приходят клиенты?
                        </Label>
                        <Input
                            id="sl-source"
                            value={form.clientSource}
                            onChange={(e) => set('clientSource')(e.target.value)}
                            placeholder="Реклама, рекомендации, звонки, офлайн"
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label htmlFor="sl-tools" className="text-xs font-semibold text-muted-foreground">
                            Что используется сейчас?
                        </Label>
                        <Input
                            id="sl-tools"
                            value={form.currentTools}
                            onChange={(e) => set('currentTools')(e.target.value)}
                            placeholder="Мессенджеры, таблицы, 1С, CRM"
                        />
                    </div>

                    <Choice label="CRM" value={form.hasCrm} options={OPTIONS.hasCrm} onChange={set('hasCrm')} />
                    <Choice label="Сайт" value={form.hasSite} options={OPTIONS.hasSite} onChange={set('hasSite')} />
                    <Choice label="Приложение" value={form.needsApp} options={OPTIONS.needsApp} onChange={set('needsApp')} />

                    <div className="flex flex-col gap-2">
                        <Label htmlFor="sl-improve" className="text-xs font-semibold text-muted-foreground">
                            Что хотелось бы улучшить?
                        </Label>
                        <Textarea
                            id="sl-improve"
                            rows={3}
                            value={form.improve}
                            onChange={(e) => set('improve')(e.target.value)}
                            placeholder="Например: не теряются заявки, клиенты возвращаются"
                        />
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2">
                        <div className="flex flex-col gap-2">
                            <Label htmlFor="sl-name" className="text-xs font-semibold text-muted-foreground">
                                Имя
                            </Label>
                            <Input id="sl-name" value={form.name} onChange={(e) => set('name')(e.target.value)} />
                        </div>
                        <div className="flex flex-col gap-2">
                            <Label htmlFor="sl-contact" className="text-xs font-semibold text-muted-foreground">
                                Телефон или email
                            </Label>
                            <Input id="sl-contact" value={form.contact} onChange={(e) => set('contact')(e.target.value)} />
                        </div>
                    </div>

                    <Button type="submit" disabled={submitting} className="w-full">
                        {submitting ? 'Отправляем…' : 'Отправить заявку'}
                    </Button>
                </form>
            </DialogContent>
        </Dialog>
    )
}

export default SolutionLeadDialog
